import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import './Admin.css';

function AdminUsers() {
    const { user: currentUser } = useAuth();
    const isAdminUser = currentUser?.role === 'admin';
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filterRole, setFilterRole] = useState('');
    const [filterActive, setFilterActive] = useState('');
    const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
    const [message, setMessage] = useState({ type: '', text: '' });

    // Modal states
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showPermissionModal, setShowPermissionModal] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [showBanModal, setShowBanModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);

    // Ban form state
    const [banData, setBanData] = useState({
        reason: '',
        banned_until: '',
        isPermanent: false
    });

    // Form states
    const [formData, setFormData] = useState({
        cid: '', password: '', fullname: '', role_id: '', is_active: true, use_name_override: false
    });

    // Permission states
    const [allPermissions, setAllPermissions] = useState({ grouped: {}, categories: [] });
    const [userPermissions, setUserPermissions] = useState({ role_permissions: [], special_permissions: [] });

    // Employee search
    const [employeeSearch, setEmployeeSearch] = useState('');
    const [employeeResults, setEmployeeResults] = useState([]);
    const [searchingEmployee, setSearchingEmployee] = useState(false);

    // ชื่อปัจจุบันจากฐานเงินเดือน (แสดงใน modal แก้ไข เพื่อ sync ชื่อที่ HR แก้แล้ว)
    const [empNameInfo, setEmpNameInfo] = useState(null);
    // เทียบชื่อแบบตัดช่องว่างซ้ำ (ฐานเงินเดือนมักคั่นชื่อ-สกุลด้วยช่องว่าง 2 ตัว)
    const normalizeName = (s) => String(s || '').replace(/\s+/g, ' ').trim();

    const fetchUsers = useCallback(async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams({
                page: pagination.page,
                limit: pagination.limit,
                search,
                role_id: filterRole,
                is_active: filterActive,
                type: 'users' // ขอเฉพาะ users ที่ไม่ใช่ staff
            });

            const [usersRes, rolesRes] = await Promise.all([
                api.get(`/users?${params}`),
                api.get('/roles')
            ]);

            setUsers(usersRes.data.users);
            setPagination(usersRes.data.pagination);
            setRoles(rolesRes.data);
        } catch (err) {
            setMessage({ type: 'error', text: 'เกิดข้อผิดพลาดในการโหลดข้อมูล' });
        } finally {
            setLoading(false);
        }
    }, [pagination.page, pagination.limit, search, filterRole, filterActive]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    // Search employees
    const searchEmployees = async (query) => {
        if (query.length < 2) {
            setEmployeeResults([]);
            return;
        }

        setSearchingEmployee(true);
        try {
            const res = await api.get(`/users/search/employees?q=${encodeURIComponent(query)}`);
            setEmployeeResults(res.data);
        } catch (err) {
            console.error('Search error:', err);
        } finally {
            setSearchingEmployee(false);
        }
    };

    // Select employee from search
    const selectEmployee = (emp) => {
        setFormData({
            ...formData,
            cid: emp.cid,
            fullname: emp.fullname
        });
        setEmployeeResults([]);
        setEmployeeSearch('');
    };

    // Create user
    const handleCreate = async (e) => {
        e.preventDefault();
        try {
            await api.post('/users', {
                cid: formData.cid,
                password: formData.password,
                fullname: formData.fullname,
                role_id: formData.role_id
            });
            setMessage({ type: 'success', text: 'สร้างผู้ใช้สำเร็จ' });
            setShowCreateModal(false);
            setFormData({ cid: '', password: '', fullname: '', role_id: '', is_active: true });
            fetchUsers();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาด' });
        }
    };

    // Update user
    const handleUpdate = async (e) => {
        e.preventDefault();
        try {
            await api.put(`/users/${selectedUser.id}`, {
                fullname: formData.fullname,
                role_id: formData.role_id || null,
                is_active: formData.is_active,
                use_name_override: !!formData.use_name_override
            });
            setMessage({ type: 'success', text: 'อัพเดทผู้ใช้สำเร็จ' });
            setShowEditModal(false);
            fetchUsers();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาด' });
        }
    };

    // Reset password
    const handleResetPassword = async (e) => {
        e.preventDefault();
        try {
            await api.post(`/users/${selectedUser.id}/reset-password`, {
                new_password: formData.password
            });
            setMessage({ type: 'success', text: 'รีเซ็ตรหัสผ่านสำเร็จ' });
            setShowPasswordModal(false);
            setFormData({ ...formData, password: '' });
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาด' });
        }
    };

    // ปิด 2FA ให้ผู้ใช้ (ทำมือถือหาย/เปลี่ยนเครื่อง) — admin เท่านั้น
    const handleReset2fa = async () => {
        if (!window.confirm(`ปิดการยืนยัน 2 ชั้น (2FA) ของ "${selectedUser.fullname}" หรือไม่?\nควรยืนยันตัวตนเจ้าของบัญชีก่อน — ผู้ใช้ต้องตั้งค่า 2FA ใหม่เองจากหน้าโปรไฟล์`)) return;
        try {
            await api.post(`/users/${selectedUser.id}/reset-2fa`);
            setSelectedUser({ ...selectedUser, twofa_enabled: false });
            setMessage({ type: 'success', text: `ปิด 2FA ของ ${selectedUser.fullname} สำเร็จ` });
            fetchUsers();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาด' });
        }
    };

    // Delete user
    const handleDelete = async (user) => {
        if (!window.confirm(`ต้องการลบผู้ใช้ "${user.fullname}" หรือไม่?`)) return;

        try {
            await api.delete(`/users/${user.id}`);
            setMessage({ type: 'success', text: 'ลบผู้ใช้สำเร็จ' });
            fetchUsers();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาด' });
        }
    };

    // Unlock user
    const handleUnlock = async (user) => {
        try {
            await api.post(`/users/${user.id}/unlock`);
            setMessage({ type: 'success', text: 'ปลดล็อคบัญชีสำเร็จ' });
            fetchUsers();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาด' });
        }
    };

    // Ban user
    const handleBan = async (e) => {
        e.preventDefault();
        try {
            await api.put(`/admin/users/${selectedUser.id}/ban`, {
                reason: banData.reason,
                banned_until: banData.isPermanent ? null : banData.banned_until || null
            });
            setMessage({ type: 'success', text: `แบนผู้ใช้ ${selectedUser.fullname} สำเร็จ` });
            setShowBanModal(false);
            setBanData({ reason: '', banned_until: '', isPermanent: false });
            fetchUsers();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาด' });
        }
    };

    // Unban user
    const handleUnban = async (user) => {
        if (!window.confirm(`ต้องการยกเลิกการแบน "${user.fullname}" หรือไม่?`)) return;

        try {
            await api.put(`/admin/users/${user.id}/unban`);
            setMessage({ type: 'success', text: `ยกเลิกการแบน ${user.fullname} สำเร็จ` });
            fetchUsers();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาด' });
        }
    };

    // Open ban modal
    const openBanModal = (user) => {
        setSelectedUser(user);
        setBanData({ reason: '', banned_until: '', isPermanent: false });
        setShowBanModal(true);
    };

    // Open edit modal
    const openEditModal = (user) => {
        setSelectedUser(user);
        setFormData({
            fullname: user.fullname,
            role_id: user.role_id || '',
            is_active: user.is_active,
            // มี fullname_override = เคยติ๊กให้ชื่อนี้ทับชื่อจากฐานเงินเดือนบนสลิป/เอกสารไว้
            use_name_override: !!(user.fullname_override || '').trim()
        });
        setShowEditModal(true);

        // ดึงชื่อปัจจุบันจากฐานเงินเดือนมาเทียบ (ชื่อตอน login ใช้สำเนาใน account.users)
        setEmpNameInfo(null);
        api.get(`/users/${user.id}/employee-name`)
            .then(res => setEmpNameInfo(res.data))
            .catch(() => setEmpNameInfo(null));
    };

    // Open permission modal
    const openPermissionModal = async (user) => {
        setSelectedUser(user);
        try {
            const [permRes, userPermRes] = await Promise.all([
                api.get('/roles/permissions/all'),
                api.get(`/users/${user.id}/permissions`)
            ]);
            setAllPermissions(permRes.data);
            setUserPermissions(userPermRes.data);
            setShowPermissionModal(true);
        } catch (err) {
            setMessage({ type: 'error', text: 'เกิดข้อผิดพลาดในการโหลดสิทธิ์' });
        }
    };

    // Toggle special permission
    const toggleSpecialPermission = async (permissionId, currentState) => {
        try {
            if (currentState === undefined) {
                await api.post(`/users/${selectedUser.id}/permissions`, {
                    permission_id: permissionId,
                    granted: true
                });
            } else if (currentState === true) {
                await api.post(`/users/${selectedUser.id}/permissions`, {
                    permission_id: permissionId,
                    granted: false
                });
            } else {
                await api.delete(`/users/${selectedUser.id}/permissions/${permissionId}`);
            }

            const userPermRes = await api.get(`/users/${selectedUser.id}/permissions`);
            setUserPermissions(userPermRes.data);
        } catch (err) {
            setMessage({ type: 'error', text: 'เกิดข้อผิดพลาด' });
        }
    };

    // Get permission state for user
    const getPermissionState = (perm) => {
        const roleHas = userPermissions.role_permissions?.some(rp => rp.id === perm.id);
        const special = userPermissions.special_permissions?.find(sp => sp.permission_id === perm.id);

        if (special) {
            return { fromRole: roleHas, special: special.granted };
        }
        return { fromRole: roleHas, special: undefined };
    };

    const categoryLabels = {
        dashboard: 'แดชบอร์ด',
        salary: 'สลิปเงินเดือน',
        tax: 'หนังสือ 50 ทวิ',
        request: 'คำร้อง',
        chat: 'แชท',
        report: 'รายงาน',
        users: 'จัดการผู้ใช้',
        roles: 'จัดการ Role',
        settings: 'ตั้งค่า',
        system: 'ระบบ'
    };

    return (
        <div className="admin-page">
            {/* Breadcrumb */}
            <div className="breadcrumb">
                <Link to="/admin">จัดการระบบ</Link>
                <span>/</span>
                <span>จัดการผู้ใช้</span>
            </div>

            <div className="admin-header">
                <div>
                    <h1>จัดการผู้ใช้งาน</h1>
                    <p>ผู้ใช้ทั่วไป {pagination.total} คน (เข้าสู่ระบบด้วยเลขบัตรประชาชน)</p>
                </div>
                <button className="steam-btn steam-btn-primary" onClick={() => {
                    setFormData({ cid: '', password: '', fullname: '', role_id: '', is_active: true });
                    setShowCreateModal(true);
                }}>
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                        <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                    </svg>
                    เพิ่มผู้ใช้
                </button>
            </div>

            {/* Info Box */}
            <div className="info-box info-box-blue mb-3">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
                </svg>
                <div>
                    <strong>ผู้ใช้ทั่วไป</strong> คือบุคลากรที่เข้าสู่ระบบด้วยเลขบัตรประชาชน + รหัสสลิป
                    &nbsp;|&nbsp;
                    <Link to="/admin/staff" className="info-link">จัดการเจ้าหน้าที่ระบบ (login ด้วย username)</Link>
                </div>
            </div>

            {message.text && (
                <div className={`alert alert-${message.type} mb-3`}>
                    {message.text}
                    <button onClick={() => setMessage({ type: '', text: '' })} style={{ float: 'right', border: 'none', background: 'none', cursor: 'pointer' }}>&times;</button>
                </div>
            )}

            {/* Filters */}
            <div className="steam-card mb-3">
                <div className="steam-card-body">
                    <div className="filters-row">
                        <div className="filter-group">
                            <input
                                type="text"
                                className="steam-input"
                                placeholder="ค้นหาชื่อ, เลขบัตรประชาชน..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                        <div className="filter-group">
                            <select
                                className="steam-input"
                                value={filterRole}
                                onChange={(e) => setFilterRole(e.target.value)}
                            >
                                <option value="">-- ทุก Role --</option>
                                {roles.map(role => (
                                    <option key={role.id} value={role.id}>{role.display_name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="filter-group">
                            <select
                                className="steam-input"
                                value={filterActive}
                                onChange={(e) => setFilterActive(e.target.value)}
                            >
                                <option value="">-- ทุกสถานะ --</option>
                                <option value="true">ใช้งาน</option>
                                <option value="false">ระงับ</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            {/* Users Table */}
            <div className="steam-card">
                <div className="steam-card-body" style={{ padding: 0, overflowX: 'auto' }}>
                    {loading ? (
                        <div className="flex-center" style={{ padding: '40px' }}>
                            <div className="spinner"></div>
                        </div>
                    ) : (
                        <table className="steam-table">
                            <thead>
                                <tr>
                                    <th>ผู้ใช้</th>
                                    <th>เลขบัตรประชาชน</th>
                                    <th>Role</th>
                                    <th>สถานะ</th>
                                    <th>เข้าสู่ระบบล่าสุด</th>
                                    <th style={{ width: '180px' }}>จัดการ</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" style={{ textAlign: 'center', padding: '40px' }}>ไม่พบข้อมูล</td>
                                    </tr>
                                ) : users.map(user => (
                                    <tr key={user.id}>
                                        <td>
                                            <div className="user-cell">
                                                <div className={`user-avatar-small ${user.avatar_url ? 'has-image' : ''}`}>
                                                    {user.avatar_url ? (
                                                        <img
                                                            src={`/api${user.avatar_url}`}
                                                            alt={user.fullname || 'avatar'}
                                                            onError={(e) => {
                                                                // ถ้ารูปโหลดไม่ได้ ให้ fallback เป็นตัวอักษรแรก
                                                                e.target.style.display = 'none';
                                                                e.target.parentNode.classList.remove('has-image');
                                                                e.target.parentNode.textContent = user.fullname?.charAt(0) || '?';
                                                            }}
                                                        />
                                                    ) : (
                                                        user.fullname?.charAt(0) || '?'
                                                    )}
                                                </div>
                                                <div>
                                                    <div className="user-name">{user.fullname}</div>
                                                    {user.emp_code && (
                                                        <div className="user-code">รหัส: {user.emp_code}</div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <span className="cid-display">{user.cid}</span>
                                        </td>
                                        <td>
                                            {user.role_display ? (
                                                <span className="badge badge-primary">{user.role_display}</span>
                                            ) : (
                                                <span className="text-muted">ผู้ใช้ทั่วไป</span>
                                            )}
                                        </td>
                                        <td>
                                            {user.is_banned ? (
                                                <div>
                                                    <span className="badge badge-danger">ถูกแบน</span>
                                                    {user.banned_until && (
                                                        <div style={{ fontSize: '11px', color: '#dc2626', marginTop: '2px' }}>
                                                            ถึง {new Date(user.banned_until).toLocaleDateString('th-TH')}
                                                        </div>
                                                    )}
                                                </div>
                                            ) : user.locked_until ? (
                                                <span className="badge badge-warning">ถูกล็อค</span>
                                            ) : user.is_active ? (
                                                <span className="badge badge-success">ใช้งาน</span>
                                            ) : (
                                                <span className="badge badge-secondary">ระงับ</span>
                                            )}
                                            {!!user.twofa_enabled && (
                                                <span className="badge badge-info" style={{ marginLeft: 4 }} title="เปิดการยืนยัน 2 ชั้น">2FA</span>
                                            )}
                                        </td>
                                        <td>
                                            {user.last_login ? new Date(user.last_login).toLocaleString('th-TH') : '-'}
                                        </td>
                                        <td>
                                            <div className="action-buttons">
                                                <button className="action-btn" title="แก้ไข" onClick={() => openEditModal(user)}>
                                                    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                                                        <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34a.9959.9959 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                                                    </svg>
                                                </button>
                                                <button className="action-btn" title="สิทธิ์พิเศษ" onClick={() => openPermissionModal(user)}>
                                                    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                                                        <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
                                                    </svg>
                                                </button>
                                                <button className="action-btn" title="รีเซ็ตรหัสผ่าน" onClick={() => {
                                                    setSelectedUser(user);
                                                    setFormData({ ...formData, password: '' });
                                                    setShowPasswordModal(true);
                                                }}>
                                                    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                                                        <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
                                                    </svg>
                                                </button>
                                                {user.locked_until && (
                                                    <button className="action-btn success" title="ปลดล็อค" onClick={() => handleUnlock(user)}>
                                                        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                                                            <path d="M12 17c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm6-9h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6h1.9c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm0 12H6V10h12v10z"/>
                                                        </svg>
                                                    </button>
                                                )}
                                                {user.is_banned ? (
                                                    <button className="action-btn success" title="ยกเลิกแบน" onClick={() => handleUnban(user)}>
                                                        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                                                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11H7v-2h10v2z"/>
                                                        </svg>
                                                    </button>
                                                ) : (
                                                    <button className="action-btn warning" title="แบนผู้ใช้" onClick={() => openBanModal(user)}>
                                                        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                                                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zM4 12c0-4.42 3.58-8 8-8 1.85 0 3.55.63 4.9 1.69L5.69 16.9C4.63 15.55 4 13.85 4 12zm8 8c-1.85 0-3.55-.63-4.9-1.69L18.31 7.1C19.37 8.45 20 10.15 20 12c0 4.42-3.58 8-8 8z"/>
                                                        </svg>
                                                    </button>
                                                )}
                                                <button className="action-btn danger" title="ลบ" onClick={() => handleDelete(user)}>
                                                    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                                                        <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                                                    </svg>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                    <div className="pagination">
                        <button
                            className="page-btn"
                            disabled={pagination.page === 1}
                            onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                        >
                            &laquo; ก่อนหน้า
                        </button>
                        <span style={{ margin: '0 16px', color: '#6b7280' }}>
                            หน้า {pagination.page} / {pagination.totalPages}
                        </span>
                        <button
                            className="page-btn"
                            disabled={pagination.page === pagination.totalPages}
                            onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                        >
                            ถัดไป &raquo;
                        </button>
                    </div>
                )}
            </div>

            {/* Create Modal */}
            {showCreateModal && (
                <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
                    <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>เพิ่มผู้ใช้ใหม่</h3>
                            <button className="modal-close" onClick={() => setShowCreateModal(false)}>&times;</button>
                        </div>
                        <form onSubmit={handleCreate}>
                            <div className="modal-body">
                                {/* Employee Search */}
                                <div className="form-group" style={{ position: 'relative' }}>
                                    <label className="form-label">ค้นหาพนักงาน (จากฐานข้อมูลเงินเดือน)</label>
                                    <input
                                        type="text"
                                        className="steam-input"
                                        placeholder="พิมพ์ชื่อ หรือเลขบัตรประชาชน..."
                                        value={employeeSearch}
                                        onChange={(e) => {
                                            setEmployeeSearch(e.target.value);
                                            searchEmployees(e.target.value);
                                        }}
                                    />
                                    {searchingEmployee && <div style={{ fontSize: '12px', color: '#6b7280' }}>กำลังค้นหา...</div>}
                                    {employeeResults.length > 0 && (
                                        <div className="employee-results">
                                            {employeeResults.map(emp => (
                                                <div key={emp.cid} className="employee-item" onClick={() => selectEmployee(emp)}>
                                                    <div style={{ fontWeight: 500 }}>{emp.fullname}</div>
                                                    <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                                        {emp.cid} | {emp.depts || emp.dept_code}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div className="form-divider">หรือกรอกข้อมูลเอง</div>

                                <div className="form-group">
                                    <label className="form-label">เลขบัตรประชาชน *</label>
                                    <input
                                        type="text"
                                        className="steam-input"
                                        value={formData.cid}
                                        onChange={(e) => setFormData({ ...formData, cid: e.target.value })}
                                        maxLength="13"
                                        required
                                        placeholder="13 หลัก"
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">ชื่อ-นามสกุล *</label>
                                    <input
                                        type="text"
                                        className="steam-input"
                                        value={formData.fullname}
                                        onChange={(e) => setFormData({ ...formData, fullname: e.target.value })}
                                        required
                                    />
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">รหัสผ่าน *</label>
                                        <input
                                            type="password"
                                            className="steam-input"
                                            value={formData.password}
                                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                            required
                                            minLength="6"
                                            placeholder="อย่างน้อย 6 ตัวอักษร"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Role (ถ้ามี)</label>
                                        <select
                                            className="steam-input"
                                            value={formData.role_id}
                                            onChange={(e) => setFormData({ ...formData, role_id: e.target.value })}
                                        >
                                            <option value="">-- ผู้ใช้ทั่วไป --</option>
                                            {roles.map(role => (
                                                <option key={role.id} value={role.id}>{role.display_name}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="steam-btn steam-btn-secondary" onClick={() => setShowCreateModal(false)}>
                                    ยกเลิก
                                </button>
                                <button type="submit" className="steam-btn steam-btn-primary">
                                    สร้างผู้ใช้
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit Modal */}
            {showEditModal && selectedUser && (
                <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
                    <div className="modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>แก้ไขผู้ใช้</h3>
                            <button className="modal-close" onClick={() => setShowEditModal(false)}>&times;</button>
                        </div>
                        <form onSubmit={handleUpdate}>
                            <div className="modal-body">
                                <p className="text-muted mb-3">
                                    เลขบัตร: <strong>{selectedUser.cid}</strong>
                                </p>

                                <div className="form-group">
                                    <label className="form-label">ชื่อ-นามสกุล</label>
                                    <input
                                        type="text"
                                        className="steam-input"
                                        value={formData.fullname}
                                        onChange={(e) => setFormData({ ...formData, fullname: e.target.value })}
                                    />
                                    {empNameInfo?.found && !formData.use_name_override && normalizeName(empNameInfo.fullname) !== normalizeName(formData.fullname) && (
                                        <div className="emp-name-sync warn">
                                            <div>
                                                ชื่อในฐานเงินเดือนไม่ตรงกัน:
                                                <strong> {empNameInfo.fullname}</strong>
                                            </div>
                                            <button
                                                type="button"
                                                className="steam-btn steam-btn-primary btn-sync-name"
                                                onClick={() => setFormData({ ...formData, fullname: empNameInfo.fullname })}
                                            >
                                                ใช้ชื่อจากฐานเงินเดือน
                                            </button>
                                        </div>
                                    )}
                                    {empNameInfo?.found && normalizeName(empNameInfo.fullname) === normalizeName(formData.fullname) && (
                                        <div className="emp-name-sync ok">
                                            ✓ ตรงกับชื่อในฐานเงินเดือน
                                        </div>
                                    )}

                                    {/* กรณีพนักงานเปลี่ยนชื่อแล้ว แต่ฐานเงินเดือนยังเป็นชื่อเดิม — ให้ชื่อที่กรอกทับบนสลิป/เอกสารด้วย */}
                                    <label className="name-override-toggle">
                                        <input
                                            type="checkbox"
                                            checked={!!formData.use_name_override}
                                            onChange={(e) => setFormData({ ...formData, use_name_override: e.target.checked })}
                                        />
                                        <span>
                                            ใช้ชื่อนี้บนสลิปเงินเดือน / 50 ทวิ / หนังสือรับรอง ด้วย
                                            <small>
                                                ติ๊กเมื่อพนักงานเปลี่ยนชื่อแล้วแต่ฐานเงินเดือนยังเป็นชื่อเดิม
                                                — ชื่อที่กรอกจะใช้แทนชื่อจากฐานเงินเดือนทุกเอกสาร (ไม่แก้ฐานเงินเดือน)
                                                {formData.use_name_override && empNameInfo?.found && (
                                                    <> · ฐานเงินเดือน: <strong>{empNameInfo.fullname}</strong></>
                                                )}
                                            </small>
                                        </span>
                                    </label>
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Role</label>
                                    <select
                                        className="steam-input"
                                        value={formData.role_id}
                                        onChange={(e) => setFormData({ ...formData, role_id: e.target.value })}
                                    >
                                        <option value="">-- ผู้ใช้ทั่วไป --</option>
                                        {roles.map(role => (
                                            <option key={role.id} value={role.id}>{role.display_name}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label className="toggle-label">
                                        <input
                                            type="checkbox"
                                            checked={formData.is_active}
                                            onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                        />
                                        <span className="toggle-switch"></span>
                                        เปิดใช้งาน
                                    </label>
                                </div>

                                {isAdminUser && (
                                    <div className="form-group twofa-admin-row">
                                        <div>
                                            <div className="form-label" style={{ marginBottom: 2 }}>การยืนยัน 2 ชั้น (2FA)</div>
                                            {selectedUser.twofa_enabled ? (
                                                <span className="badge badge-info">เปิดใช้งานอยู่</span>
                                            ) : (
                                                <span className="text-muted" style={{ fontSize: 13 }}>ไม่ได้เปิด</span>
                                            )}
                                        </div>
                                        {!!selectedUser.twofa_enabled && (
                                            <button type="button" className="steam-btn steam-btn-secondary" onClick={handleReset2fa}>
                                                ปิด 2FA
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="steam-btn steam-btn-secondary" onClick={() => setShowEditModal(false)}>
                                    ยกเลิก
                                </button>
                                <button type="submit" className="steam-btn steam-btn-primary">
                                    บันทึก
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Password Reset Modal */}
            {showPasswordModal && selectedUser && (
                <div className="modal-overlay" onClick={() => setShowPasswordModal(false)}>
                    <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>รีเซ็ตรหัสผ่าน</h3>
                            <button className="modal-close" onClick={() => setShowPasswordModal(false)}>&times;</button>
                        </div>
                        <form onSubmit={handleResetPassword}>
                            <div className="modal-body">
                                <p className="text-muted mb-3">รีเซ็ตรหัสผ่านสำหรับ: {selectedUser.fullname}</p>
                                <div className="form-group">
                                    <label className="form-label">รหัสผ่านใหม่</label>
                                    <input
                                        type="password"
                                        className="steam-input"
                                        value={formData.password}
                                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                        required
                                        minLength="6"
                                        placeholder="อย่างน้อย 6 ตัวอักษร"
                                    />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="steam-btn steam-btn-secondary" onClick={() => setShowPasswordModal(false)}>
                                    ยกเลิก
                                </button>
                                <button type="submit" className="steam-btn steam-btn-primary">
                                    รีเซ็ตรหัสผ่าน
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Permission Modal */}
            {showPermissionModal && selectedUser && (
                <div className="modal-overlay" onClick={() => setShowPermissionModal(false)}>
                    <div className="modal modal-xl" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>สิทธิ์พิเศษ: {selectedUser.fullname}</h3>
                            <button className="modal-close" onClick={() => setShowPermissionModal(false)}>&times;</button>
                        </div>
                        <div className="modal-body permission-modal-body">
                            <div className="permission-info">
                                <p><strong>Role ปัจจุบัน:</strong> {selectedUser.role_display || 'ผู้ใช้ทั่วไป'}</p>
                                <p className="text-muted" style={{ fontSize: '13px' }}>
                                    สิทธิ์พิเศษจะเพิ่มหรือลบสิทธิ์นอกเหนือจาก Role ที่กำหนด
                                </p>
                            </div>

                            <div className="permission-legend">
                                <span className="legend-item">
                                    <span className="legend-box from-role"></span> จาก Role
                                </span>
                                <span className="legend-item">
                                    <span className="legend-box granted"></span> เพิ่มพิเศษ
                                </span>
                                <span className="legend-item">
                                    <span className="legend-box revoked"></span> ยกเลิก
                                </span>
                            </div>

                            <div className="permissions-grid">
                                {Object.entries(allPermissions.grouped || {}).map(([category, perms]) => (
                                    <div key={category} className="permission-category">
                                        <h4>{categoryLabels[category] || category}</h4>
                                        <div className="permission-list">
                                            {perms.map(perm => {
                                                const state = getPermissionState(perm);
                                                return (
                                                    <div
                                                        key={perm.id}
                                                        className={`permission-item ${state.fromRole ? 'from-role' : ''} ${state.special === true ? 'special-granted' : ''} ${state.special === false ? 'special-revoked' : ''}`}
                                                    >
                                                        <div className="perm-info">
                                                            <div className="perm-name">{perm.display_name}</div>
                                                            <div className="perm-desc">{perm.description}</div>
                                                        </div>
                                                        <div className="perm-actions">
                                                            {state.fromRole && state.special === undefined && (
                                                                <span className="perm-badge role">จาก Role</span>
                                                            )}
                                                            {state.special === true && (
                                                                <span className="perm-badge granted">เพิ่มพิเศษ</span>
                                                            )}
                                                            {state.special === false && (
                                                                <span className="perm-badge revoked">ยกเลิก</span>
                                                            )}
                                                            <button
                                                                type="button"
                                                                className="btn-perm-toggle"
                                                                onClick={() => toggleSpecialPermission(perm.id, state.special)}
                                                                title={
                                                                    state.special === undefined
                                                                        ? 'เพิ่มสิทธิ์พิเศษ'
                                                                        : state.special === true
                                                                            ? 'เปลี่ยนเป็นยกเลิก'
                                                                            : 'ลบสิทธิ์พิเศษ'
                                                                }
                                                            >
                                                                {state.special === undefined ? '+' : state.special === true ? '−' : '×'}
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="steam-btn steam-btn-secondary" onClick={() => setShowPermissionModal(false)}>
                                ปิด
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Ban Modal */}
            {showBanModal && selectedUser && (
                <div className="modal-overlay" onClick={() => setShowBanModal(false)}>
                    <div className="modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header modal-header-danger">
                            <h3>แบนผู้ใช้</h3>
                            <button className="modal-close" onClick={() => setShowBanModal(false)}>&times;</button>
                        </div>
                        <form onSubmit={handleBan}>
                            <div className="modal-body">
                                <div className="ban-warning-box">
                                    <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zM4 12c0-4.42 3.58-8 8-8 1.85 0 3.55.63 4.9 1.69L5.69 16.9C4.63 15.55 4 13.85 4 12zm8 8c-1.85 0-3.55-.63-4.9-1.69L18.31 7.1C19.37 8.45 20 10.15 20 12c0 4.42-3.58 8-8 8z"/>
                                    </svg>
                                    <div>
                                        <strong>{selectedUser.fullname}</strong>
                                        <p>ผู้ใช้จะไม่สามารถเข้าสู่ระบบได้จนกว่าจะยกเลิกการแบน</p>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="form-label">เหตุผลในการแบน *</label>
                                    <textarea
                                        className="steam-input"
                                        rows="3"
                                        value={banData.reason}
                                        onChange={(e) => setBanData({ ...banData, reason: e.target.value })}
                                        required
                                        placeholder="ระบุเหตุผลที่แบนผู้ใช้นี้..."
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="toggle-label">
                                        <input
                                            type="checkbox"
                                            checked={banData.isPermanent}
                                            onChange={(e) => setBanData({ ...banData, isPermanent: e.target.checked })}
                                        />
                                        <span className="toggle-switch"></span>
                                        แบนถาวร (ไม่มีวันหมดอายุ)
                                    </label>
                                </div>

                                {!banData.isPermanent && (
                                    <div className="form-group">
                                        <label className="form-label">แบนถึงวันที่</label>
                                        <input
                                            type="datetime-local"
                                            className="steam-input"
                                            value={banData.banned_until}
                                            onChange={(e) => setBanData({ ...banData, banned_until: e.target.value })}
                                            min={new Date().toISOString().slice(0, 16)}
                                        />
                                        <small className="text-muted">หากไม่ระบุ จะเป็นการแบนถาวร</small>
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="steam-btn steam-btn-secondary" onClick={() => setShowBanModal(false)}>
                                    ยกเลิก
                                </button>
                                <button type="submit" className="steam-btn steam-btn-danger">
                                    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" style={{ marginRight: 6 }}>
                                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zM4 12c0-4.42 3.58-8 8-8 1.85 0 3.55.63 4.9 1.69L5.69 16.9C4.63 15.55 4 13.85 4 12zm8 8c-1.85 0-3.55-.63-4.9-1.69L18.31 7.1C19.37 8.45 20 10.15 20 12c0 4.42-3.58 8-8 8z"/>
                                    </svg>
                                    แบนผู้ใช้
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <style>{`
                .admin-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 20px;
                }
                .admin-header h1 { margin: 0 0 4px 0; }
                .admin-header p { margin: 0; color: #6b7280; }
                .info-box-blue {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 14px 18px;
                    background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
                    border: 1px solid #93c5fd;
                    border-radius: 10px;
                    color: #1e40af;
                    font-size: 14px;
                }
                .info-link {
                    color: #2563eb;
                    text-decoration: underline;
                    font-weight: 500;
                }
                .info-link:hover {
                    color: #1d4ed8;
                }
                .emp-name-sync {
                    margin-top: 8px;
                    padding: 10px 12px;
                    border-radius: 8px;
                    font-size: 13px;
                }
                .emp-name-sync.warn {
                    background: #fffbeb;
                    border: 1px solid #fcd34d;
                    color: #92400e;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 10px;
                    flex-wrap: wrap;
                }
                .emp-name-sync.ok {
                    background: #ecfdf5;
                    border: 1px solid #6ee7b7;
                    color: #065f46;
                }
                .name-override-toggle {
                    display: flex;
                    align-items: flex-start;
                    gap: 10px;
                    margin-top: 10px;
                    padding: 10px 12px;
                    border-radius: 8px;
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    font-size: 13px;
                    cursor: pointer;
                }
                .name-override-toggle input {
                    margin-top: 3px;
                    flex-shrink: 0;
                }
                .name-override-toggle small {
                    display: block;
                    margin-top: 2px;
                    color: #64748b;
                    font-size: 12px;
                    line-height: 1.4;
                }
                .twofa-admin-row {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 10px;
                    padding: 10px 12px;
                    border-radius: 8px;
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                }
                .btn-sync-name {
                    padding: 6px 12px;
                    font-size: 13px;
                    white-space: nowrap;
                }
                .filters-row {
                    display: flex;
                    gap: 12px;
                    flex-wrap: wrap;
                }
                .filter-group {
                    flex: 1;
                    min-width: 180px;
                }
                .user-cell {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }
                .user-code {
                    font-size: 12px;
                    color: #6b7280;
                }
                .cid-display {
                    font-family: 'Consolas', 'Monaco', monospace;
                    font-size: 13px;
                    color: #4b5563;
                }
                .badge-info { background: #e0e7ff; color: #3730a3; }
                .badge-secondary { background: #f3f4f6; color: #4b5563; }
                .action-buttons {
                    display: flex;
                    gap: 4px;
                }
                .action-btn.success { color: #059669; }
                .action-btn.success:hover { background: #d1fae5; }
                .action-btn.warning { color: #d97706; }
                .action-btn.warning:hover { background: #fef3c7; }
                .badge-warning { background: #fef3c7; color: #92400e; }
                .modal-header-danger {
                    background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
                    border-bottom: 1px solid #fca5a5;
                }
                .modal-header-danger h3 { color: #991b1b; }
                .ban-warning-box {
                    display: flex;
                    gap: 12px;
                    padding: 16px;
                    background: #fef2f2;
                    border: 1px solid #fecaca;
                    border-radius: 10px;
                    margin-bottom: 20px;
                    color: #dc2626;
                }
                .ban-warning-box strong { display: block; margin-bottom: 4px; color: #991b1b; }
                .ban-warning-box p { margin: 0; font-size: 13px; }
                .steam-btn-danger {
                    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
                    color: white;
                    border: none;
                    display: flex;
                    align-items: center;
                }
                .steam-btn-danger:hover { background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%); }
                .modal-lg { max-width: 600px; }
                .modal-xl { max-width: 900px; }
                .modal-sm { max-width: 400px; }
                .form-row {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 16px;
                }
                .form-divider {
                    text-align: center;
                    margin: 20px 0;
                    color: #9ca3af;
                    position: relative;
                }
                .form-divider::before, .form-divider::after {
                    content: '';
                    position: absolute;
                    top: 50%;
                    width: 35%;
                    height: 1px;
                    background: #e5e7eb;
                }
                .form-divider::before { left: 0; }
                .form-divider::after { right: 0; }
                .employee-results {
                    position: absolute;
                    top: 100%;
                    left: 0;
                    right: 0;
                    background: white;
                    border: 1px solid #e5e7eb;
                    border-radius: 8px;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                    max-height: 200px;
                    overflow-y: auto;
                    z-index: 10;
                }
                .employee-item {
                    padding: 10px 12px;
                    cursor: pointer;
                    border-bottom: 1px solid #f3f4f6;
                }
                .employee-item:hover { background: #f9fafb; }
                .toggle-label {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    cursor: pointer;
                }
                .toggle-label input { display: none; }
                .toggle-switch {
                    width: 44px;
                    height: 24px;
                    background: #e5e7eb;
                    border-radius: 12px;
                    position: relative;
                    transition: background 0.2s;
                }
                .toggle-switch::after {
                    content: '';
                    position: absolute;
                    top: 2px;
                    left: 2px;
                    width: 20px;
                    height: 20px;
                    background: white;
                    border-radius: 50%;
                    transition: transform 0.2s;
                }
                .toggle-label input:checked + .toggle-switch { background: #3b82f6; }
                .toggle-label input:checked + .toggle-switch::after { transform: translateX(20px); }
                .permission-modal-body {
                    max-height: 65vh;
                    overflow-y: auto;
                    pointer-events: auto;
                }
                .modal { pointer-events: auto; }
                .modal-overlay { pointer-events: auto; }
                .permission-info {
                    background: #f9fafb;
                    padding: 12px;
                    border-radius: 8px;
                    margin-bottom: 16px;
                }
                .permission-info p { margin: 0 0 4px 0; }
                .permission-legend {
                    display: flex;
                    gap: 20px;
                    margin-bottom: 16px;
                    padding: 8px 12px;
                    background: #f3f4f6;
                    border-radius: 6px;
                    font-size: 13px;
                }
                .legend-item {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                }
                .legend-box {
                    width: 14px;
                    height: 14px;
                    border-radius: 3px;
                    border: 2px solid;
                }
                .legend-box.from-role { border-color: #3b82f6; background: #dbeafe; }
                .legend-box.granted { border-color: #10b981; background: #d1fae5; }
                .legend-box.revoked { border-color: #ef4444; background: #fee2e2; }
                .permissions-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
                    gap: 16px;
                }
                .permission-category {
                    background: #f9fafb;
                    border-radius: 8px;
                    padding: 12px;
                }
                .permission-category h4 {
                    margin: 0 0 10px 0;
                    font-size: 14px;
                    color: #374151;
                    padding-bottom: 8px;
                    border-bottom: 1px solid #e5e7eb;
                }
                .permission-list {
                    display: flex;
                    flex-direction: column;
                    gap: 6px;
                }
                .permission-item {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 6px 8px;
                    background: white;
                    border-radius: 4px;
                    border: 2px solid transparent;
                    font-size: 13px;
                }
                .permission-item.from-role { border-color: #93c5fd; background: #eff6ff; }
                .permission-item.special-granted { border-color: #6ee7b7; background: #ecfdf5; }
                .permission-item.special-revoked { border-color: #fca5a5; background: #fef2f2; }
                .perm-info { flex: 1; min-width: 0; }
                .perm-name { font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
                .perm-desc { font-size: 11px; color: #6b7280; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
                .perm-actions {
                    display: flex;
                    align-items: center;
                    gap: 4px;
                    flex-shrink: 0;
                    margin-left: 8px;
                }
                .perm-badge {
                    font-size: 9px;
                    padding: 2px 5px;
                    border-radius: 3px;
                    white-space: nowrap;
                }
                .perm-badge.role { background: #dbeafe; color: #1e40af; }
                .perm-badge.granted { background: #d1fae5; color: #065f46; }
                .perm-badge.revoked { background: #fee2e2; color: #991b1b; }
                .btn-perm-toggle {
                    width: 28px;
                    height: 28px;
                    border: none;
                    background: #3b82f6;
                    color: white;
                    border-radius: 6px;
                    cursor: pointer;
                    font-weight: bold;
                    font-size: 16px;
                    line-height: 1;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.15s ease;
                    position: relative;
                    z-index: 5;
                }
                .btn-perm-toggle:hover { background: #2563eb; transform: scale(1.1); }
                .btn-perm-toggle:active { transform: scale(0.95); }
                .permission-item .perm-actions { pointer-events: auto; }
                @media (max-width: 768px) {
                    .form-row { grid-template-columns: 1fr; }
                    .permissions-grid { grid-template-columns: 1fr; }
                    .admin-header { flex-direction: column; gap: 12px; align-items: flex-start; }
                }
            `}</style>
        </div>
    );
}

export default AdminUsers;
